import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { getAppliedJobs } from '../../../../apiManager/shoveller/matchJobApi';
import { Card, CardContent } from '../../../ui/card';
import { Calendar, Clock, Snowflake } from 'lucide-react';
import { getStatusBadge } from '../../homeowner/jobs/detail/StatusUtil';
import { formatDate, formatPayment, formatScheduledDate, formatTime } from '../../../../lib/utils';
import { Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';

export function AppliedJobs() {
  const { userInfo } = useSelector((state) => state.auth);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const shovellerId = userInfo?.user?.id;

  useEffect(() => {
    const fetchAppliedJobs = async () => {
      if (!shovellerId) return;

      try {
        setLoading(true);
        const response = await getAppliedJobs(shovellerId);
        const sortedJobs = response.jobs.sort((a, b) => {
          const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime || '00:00'}`);
          const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime || '00:00'}`);
          return dateB - dateA;
        });

        setJobs(sortedJobs || []);
      } catch (error) {
        console.error('Error fetching applied jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppliedJobs();
  }, [shovellerId]);

  // Skeleton Loading Component
  const SkeletonCard = () => (
    <Card className="border-l-4 border-l-gray-300 animate-pulse">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex justify-between items-center gap-3 mb-2">
              <div className="h-6 bg-gray-300 rounded w-1/3"></div>
              <div className="h-6 bg-gray-300 rounded w-20"></div>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-4 bg-gray-300 rounded w-4"></div>
              <div className="h-4 bg-gray-300 rounded w-40"></div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-4 bg-gray-300 rounded w-4"></div>
              <div className="h-4 bg-gray-300 rounded w-16"></div>
            </div>
            <div className="h-4 bg-gray-300 rounded w-20"></div>
          </div>

          <div className="flex items-center justify-between">
            <div className="h-4 bg-gray-300 rounded w-20"></div>
            <div className="h-4 bg-gray-300 rounded w-16"></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderJobCard = (job) => {
    return (
      <Card key={job._id} className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500">
        <CardContent className="p-6">
          {/* Header Section */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex justify-between items-center gap-3 mb-2">
                <h3 className="text-xl font-bold text-gray-900">
                  {job.serviceDetail?.service}
                </h3>
                {getStatusBadge(job.jobStatus)}
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>Scheduled: {formatScheduledDate(job.scheduledDate)}</span>
              </div>
            </div>
          </div>

          {/* Information Rows */}
          <div className="space-y-3">
            {/* Scheduled Time */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-gray-700">Time:</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatTime(job.scheduledTime)}
              </span>
            </div>

            {/* Amount */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-gray-700">Amount:</span>
              </div>
              <span className="text-sm font-bold text-green-700">
                {formatPayment(job.paymentInfo?.amount || 0, { is80: true, isVolunteer: job.isRequestedForVolunteer, isShoveller: true })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Empty State Component
  const EmptyState = () => (
    <div className="text-center p-12">
      <div className="flex justify-center mb-4">
        <div className="relative">
          <Snowflake className="w-16 h-16 text-gray-300 mb-2" />
          <Briefcase className="w-8 h-8 text-gray-400 absolute -bottom-1 -right-1" />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-gray-600 mb-2">
        No Applied Jobs Yet
      </h3>
      <p className="text-gray-500 max-w-md mx-auto">
        You haven't applied to any jobs yet. Start browsing available jobs to find opportunities that match your skills.
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-12 py-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-5xl font-bold text-black">Applied Jobs</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Manage and track all your applied jobs.
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-12 py-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-5xl font-bold text-black">Applied Jobs</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Manage and track all your applied jobs.
        </p>
      </div>

      <div className="p-6">
        {jobs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {jobs.map((job, i) => (
              <Link key={i} to={`/helper/jobs/${job._id}`}>
                {renderJobCard(job)}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}